import React from 'react';
import { css, styled } from '@this/constants/themes';
import type SelectStore from '@this/src/domain/select_store';
import type SegmentClass from '@this/src/domain/transit/segment';
import type Transit from '@this/src/domain/transit/transit';
import { SHORT_DISTANCE_MODE, type ShortDistanceMode } from '@this/src/lib/hooks/useShortDistance';
import IndividualRouteInfo from './IndividualRouteInfo';
import PackageRouteInfo from '../package/PackageRouteInfo';
import SelectTransportBoxRightInOutlineTemplate from '../../right/right_in_outline.template';

interface IndividualDetailProps {
  transit: Transit;
  selected: boolean;
  selectedLegSummaryIndex: number | null;
  domesticAirPriceIndex: number;
  handleChange: (legSummaryIndex?: number) => void;
  store: SelectStore;
  isRequestForm?: boolean;
  segment: SegmentClass;
  segmentIdx: number;
  shortDistanceMode: ShortDistanceMode;
}

const IndividualDetail = ({
  transit,
  selected,
  selectedLegSummaryIndex,
  domesticAirPriceIndex,
  handleChange,
  store,
  isRequestForm,
  segment,
  segmentIdx,
  shortDistanceMode
}: IndividualDetailProps) => {
  const displayLegSummaries =
    shortDistanceMode === SHORT_DISTANCE_MODE.LIMITED
      ? segment.leg_summaries?.filter(legSummary => {
          const isTrainOrFlight = legSummary.type === 'jr' || legSummary.type === 'air';
          return isTrainOrFlight;
        })
      : segment.leg_summaries;

  const resolvePrice = (): number | undefined => {
    return (transit.pricesOriginal?.[domesticAirPriceIndex]?.price || transit.price) ?? undefined;
  };

  const isLegSelected = (legSummaryIndex: number, isTrainOrFlight: boolean) =>
    selected && isTrainOrFlight && selectedLegSummaryIndex === legSummaryIndex;

  const renderIndividualRightPanel = (
    isTrainOrFlight: boolean,
    legSummaryIndex: number,
    legSummaryPrice?: number
  ) => {
    const legSelected = isLegSelected(legSummaryIndex, isTrainOrFlight);

    if (isTrainOrFlight) {
      return (
        <SelectTransportBoxRightInOutlineTemplate
          price={resolvePrice()}
          isAdditional={false}
          priceRange={store.result.type === 'airPackage' ? transit.price_range ?? undefined : undefined}
          includeTax={transit.includeTax}
          selected={legSelected}
          onClick={() => handleChange(legSummaryIndex)}
          isRequestForm={isRequestForm || false}
          isRakutenPackage={transit.isRakutenPackage}
          alignPriceRight
        />
      );
    }
    return (
      <SelectTransportBoxRightInOutlineTemplate
        price={legSummaryPrice}
        isAdditional={false}
        includeTax={transit.includeTax}
        selected={false}
        onClick={() => undefined}
        isRequestForm
        isRakutenPackage={false}
        showsPricePerPersonText={false}
        showsIncludeTax={false}
        showsBackground={false}
        alignPriceRight
        priceColor="#999999"
      />
    );
  };

  if (!displayLegSummaries?.length && segment.legs?.length) {
    const hasAir = transit.airExists();
    return (
      <StyledLegSummariesContainer>
        <StyledLegSummaryItem $bordered $selected={selected}>
          {segment.legs.map((leg, i) => (
            <PackageRouteInfo
              key={`leg_fallback_${segmentIdx}_${i}`}
              leg={leg}
              legIndex={i}
              segment={segment}
              segmentIdx={segmentIdx}
              hasAir={hasAir}
            />
          ))}
          {renderIndividualRightPanel(true, 0)}
        </StyledLegSummaryItem>
      </StyledLegSummariesContainer>
    );
  }

  return (
    <StyledLegSummariesContainer>
      {displayLegSummaries?.map((legSummary, j) => {
        const isEmptyPrice = legSummary.price === 0;
        const isWalking = legSummary.name === '徒歩';
        const hasNoName = legSummary.name === '';
        const isTrainOrFlight = legSummary.type === 'jr' || legSummary.type === 'air';
        const legSelected = isLegSelected(j, isTrainOrFlight);

        const isError = isEmptyPrice && !isWalking && !hasNoName;
        if (isError) {
          return <StyledError key={`error_${segmentIdx}_${j}`}>経路の検索に失敗しました</StyledError>;
        }

        return (
          <StyledLegSummaryItem
            key={`express_short_distance_${segmentIdx}_${j}`}
            $bordered={isTrainOrFlight}
            $selected={legSelected}
          >
            <IndividualRouteInfo legSummary={legSummary} segment={segment} isTrainOrFlight={isTrainOrFlight} />
            {renderIndividualRightPanel(isTrainOrFlight, j, legSummary.price ?? 0)}
          </StyledLegSummaryItem>
        );
      })}
    </StyledLegSummariesContainer>
  );
};

const StyledError = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: ${props => props.theme.redColor};
`;

const StyledLegSummaryItem = styled.div<{ $bordered?: boolean; $selected?: boolean }>`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;

  ${props =>
    props.$bordered &&
    !props.$selected &&
    css`
      border-top: 1px solid #eee;
      border-bottom: 1px solid #eee;
    `}

  ${props =>
    props.$selected &&
    css`
      border: 2px solid ${props.theme.linkColor};
    `}
`;

const StyledLegSummariesContainer = styled.div`
  flex-shrink: 10;
  padding: 0;
  width: 100%;
`;

export default IndividualDetail;
